import {useState, useEffect, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import useBodyClass from '../../common/hooks/useBodyClassName';
import CacheData from '../../common/utils/cacheData';
import { constants } from '../../common/constants/constants';
import logoBllink from '../../images/Logo-Full-Dark.png';
import { StyledTenantDetailsWrapper } from './TenantDetails.styled';
import {getBuildingAddress} from "../../common/shared/statelessMethods";
import {useTranslation} from "react-i18next";
import HeaderMessage from "./HeaderMessage";
import {setHeaderMessageClosed} from "../../store/tenant/slice";
import {useHistory, useRouteMatch} from "react-router-dom";
import arrow from "../../images/newDesign/arrow.svg";
import classNames from "classnames";
import {NotificationManager} from "react-notifications";
import {hideLoader, showLoader} from "../../store/common/slice";
import {fetchBuildingData, fetchBuildingFeatures} from "../../store/buildings/actions";
import gurGif from "../../images/newDesign/gif/gurGif.gif";

function PaymentFlowContainer({
    title,
    subheader,
    children,
    numOfMethods,
    hideLogo = false,
    hideSubheader = false,
    hideBack = false,
    insurance = false, // TODO: add hideHeader parameter instead amd move all styling into the insurance page
    showHeaderMessage = false,
    onPageBack = () => void null,
}) {

    useBodyClass('payment-flow');

    const { t } = useTranslation();
    const dispatch = useDispatch();
    const history = useHistory();
    const match = useRouteMatch();

    const currentBuildingData = useSelector(state => state.buildings.currentBuildingData);
    const currentBuildingDataPendingId = useSelector(state => state.buildings.currentBuildingDataPendingId);
    const buildingFeaturesPendingId = useSelector(state => state.buildings.currentBuildingFeaturesPendingId);
    const mcLogo = useSelector(state => state.buildings.currentBuildingData?.managementCompany?.logo ?? logoBllink);

    const ltr = useSelector(state => state.common.ltrEnabled);
    const buildingAddress = useSelector(state => state.tenant.buildingAddress);
    const tenantDetails = useSelector(state => state.tenant.details);
    const buildingID = useSelector(state => state.tenant.buildingID);
    const headerMessageClosed = useSelector(state => state.tenant.headerMessageClosed);

    const dataReady = useMemo(() => currentBuildingData?.id == match.params.buildingID,[currentBuildingData, match]);

    useEffect(async () => {
        const matchedBuildingId = match.params.buildingID;
        if (currentBuildingDataPendingId !== matchedBuildingId) {
            dispatch(showLoader());
            await dispatch(fetchBuildingData({ buildingID: matchedBuildingId , options: {urlParams: {includeInternalID: true}}}));
            dispatch(hideLoader());
        }
    }, []);

    useEffect(async () => {
        const matchedBuildingId = match.params.buildingID;
        if (buildingFeaturesPendingId !== matchedBuildingId) {
            dispatch(showLoader());
            await dispatch(fetchBuildingFeatures({ buildingID: matchedBuildingId }));
            dispatch(hideLoader());
        }
    }, []);

    useEffect(() => window.scrollTo(0, 0), []);

    useEffect(() => {
        if (buildingID == null && dataReady) {
            // NOTE: we may loose storage.tenant.buildingID in case user open current page in another tab/window by URL
            const matchedBuildingId = match.params.buildingID;
            const firstPage = `/${constants.pages.buildingsUrl}/${matchedBuildingId}`;
            if (firstPage !== history.location.pathname) {
                window.setTimeout(() => {
                    // do it in timeout to allow system to change language according to buildingData.currency
                    if (matchedBuildingId == null) {
                        NotificationManager.warning(t('payments.contextLostAtAll'));
                    } else {
                        NotificationManager.warning(t('payments.contextLost'));
                        history.push(firstPage);
                    }
                },1);
            }
        }
    }, [currentBuildingData]);

    const address = useMemo(
        () => getBuildingAddress(t, buildingAddress, { apartmentNumber: tenantDetails?.apartmentNumber }),
        [buildingAddress, tenantDetails]
    );

    function handleBackBtnClick() {
        onPageBack();
        history.goBack();
    }
    function handleInsuranceExitBtnClick() {
        onPageBack();
    }

    function getBackButton() {
        if (insurance) {
            return <div className={classNames('back-btn', {'covered': displayHeaderMessage})} onClick={handleInsuranceExitBtnClick}>X</div>;
        }
        if (!hideBack) {
            return <div className={classNames('back-btn', {'covered': displayHeaderMessage})} onClick={handleBackBtnClick}><img src={arrow}/>{t('auto_charge.back')}</div>
        }
    }

    if (!dataReady) {
        return '';
    }

    const displayHeaderMessage = showHeaderMessage && !headerMessageClosed;
    return (
        <StyledTenantDetailsWrapper ltr={ltr} numOfMethods={numOfMethods} bankInfo={hideLogo || hideSubheader}>
            {getBackButton()}
            {displayHeaderMessage && <HeaderMessage onCloseClick={() => dispatch(setHeaderMessageClosed(true))} buildingID={buildingID} />}

            {!insurance && (<>
                {!hideLogo && <img className='mc-logo' src={mcLogo} />}
                <h1 className='page-title text-only'>
                    {title}
                    <div className='building-address'>{address}</div>
                </h1>
                {!hideSubheader && <div className='choose-action-subheader'>{subheader}</div>}
            </>)}

            {insurance && (<>
                <div className='insurance-top'>
                    <img src={gurGif} />
                    <div className='header'>{title}</div>
                    <div className='subheader'>{subheader}</div>
                </div>
            </>)}

            {children}
        </StyledTenantDetailsWrapper>
    )
}

export default PaymentFlowContainer;
